<script setup lang="ts">
/**
 * Shell for every marketing subpage carved out of the old one-page homepage.
 *
 * Takes care of the layout, SEO meta and the WebPage JSON-LD node, then renders
 * the template's inner-page frame around the section itself: the dark title
 * band on top, cross-links to the other feature pages and the closing CTA.
 */
const props = defineProps<{
  /** Key under `seo` in the translations, e.g. "aiFeatures". */
  seoKey?: string
  /**
   * Title and description given directly, for pages generated from data (one
   * per synonym, one per competitor) where there is no `seo.*` key to point at.
   * Win over `seoKey` when set.
   */
  seoTitle?: string
  seoDescription?: string
  /** A level between Home and this page in the breadcrumb, e.g. the synonyms hub. */
  parent?: { name: string, path: string }
  /** Canonical path of this page, e.g. "/ai-features". */
  path: string
  /** Visible heading in the title band when the SEO title is too long for it. */
  breadcrumbTitle?: string
  /**
   * Extra JSON-LD nodes to merge into this page's @graph, e.g. an FAQPage.
   * The graph is emitted under one key, so a page cannot call useJsonLdGraph a
   * second time without replacing what this component already put there.
   */
  extraNodes?: Array<Record<string, unknown> | null>
}>()

const { t } = useI18n()
const { canonicalFor, absUrl } = useSiteUrl()
const localize = useLocalizedPath()
const { webPageNode, breadcrumbNode, useJsonLdGraph } = useJsonLd()

const title = computed(() => props.seoTitle ?? t(`seo.${props.seoKey}.title`))
const description = computed(() => props.seoDescription ?? t(`seo.${props.seoKey}.description`))

// The <title> may end in the brand (see seo.homepage); the heading must not.
const heading = computed(() => props.breadcrumbTitle || title.value.replace(/\s*\|\s*InMyResume\s*$/, ''))

useSeoMeta({
  title: () => title.value,
  description: () => description.value,
  ogTitle: () => title.value,
  ogDescription: () => description.value,
  ogImage: () => absUrl('/og/home.png?v=2'),
  ogUrl: () => canonicalFor(localize(props.path)),
  ogSiteName: 'InMyResume',
  twitterCard: 'summary_large_image',
})

/** /pricing and /templates show the plans in full, so the note would repeat them. */
const showPackNote = computed(() => !['/pricing', '/templates'].includes(props.path))

useJsonLdGraph(() => [
  webPageNode({ path: props.path, name: title.value, description: description.value, hasBreadcrumb: true }),
  breadcrumbNode(props.path, [
    { name: t('nav.home'), path: '/' },
    ...(props.parent ? [props.parent] : []),
    { name: title.value },
  ]),
  ...(props.extraNodes ?? []),
])
</script>

<template>
  <NuxtLayout name="sell" :show-back-button="false">
    <div class="w-full">
      <SofaxBreadcrumb
        :title="heading"
        :subtitle="description"
        :items="parent ? [{ name: parent.name, to: parent.path }, { name: heading }] : [{ name: heading }]"
      />
      <slot />
      <!-- Pages that carry the price ladder themselves skip the one-liner -->
      <div v-if="showPackNote" class="shell pt-10 md:pt-14">
        <PremiumJobPackNote />
      </div>
      <MarketingFeatureLinks :exclude="path" compact />
      <SofaxPageCta />
    </div>
  </NuxtLayout>
</template>
